export interface WheelSize {
  width: number;
  aspectRatio: number;
  rimDiameter: number;
}

export const WHEEL_SIZE_LIMITS = {
  width: { min: 125, max: 395, step: 5, label: "Tyre width (mm)" },
  aspectRatio: { min: 20, max: 85, step: 5, label: "Sidewall profile (%)" },
  rimDiameter: { min: 12, max: 24, step: 1, label: "Rim diameter (inches)" },
} as const;

export function isValidWheelSize(size: WheelSize): boolean {
  return (Object.keys(WHEEL_SIZE_LIMITS) as (keyof WheelSize)[]).every((key) => {
    const value = size[key];
    const { min, max } = WHEEL_SIZE_LIMITS[key];
    return Number.isFinite(value) && value >= min && value <= max;
  });
}

export function formatWheelSize(size: WheelSize): string {
  return `${size.width}/${size.aspectRatio}R${size.rimDiameter}`;
}

export function calculateWheelSize(size: WheelSize) {
  if (!isValidWheelSize(size)) throw new RangeError("Tyre dimensions are outside the supported range.");
  const sidewall = size.width * size.aspectRatio / 100;
  const rim = size.rimDiameter * 25.4;
  const diameter = rim + 2 * sidewall;
  const circumference = diameter * Math.PI;
  return { sidewall, rim, diameter, circumference, revolutionsPerKm: 1000000 / circumference };
}

export function compareWheelSizes(original: WheelSize, alternative: WheelSize, indicatedSpeed: number) {
  if (!Number.isFinite(indicatedSpeed) || indicatedSpeed < 0 || indicatedSpeed > 200) {
    throw new RangeError("Indicated speed must be between 0 and 200 km/h.");
  }
  const before = calculateWheelSize(original);
  const after = calculateWheelSize(alternative);
  const ratio = after.diameter / before.diameter;
  return {
    before,
    after,
    diameterChange: (ratio - 1) * 100,
    clearanceChange: (after.diameter - before.diameter) / 2,
    actualSpeed: indicatedSpeed * ratio,
  };
}

export interface VehicleWheelFitment {
  id: string;
  make: string;
  model: string;
  modelAliases?: string[];
  variant: string;
  size: WheelSize;
}

// Reference examples only: factory options and market specifications can differ.
export const VEHICLE_WHEEL_FITMENTS: VehicleWheelFitment[] = [
  { id: "vw-polo-vivo-trendline", make: "Volkswagen", model: "Polo Vivo", variant: "2018-2023 1.4 Trendline", size: { width: 175, aspectRatio: 70, rimDiameter: 14 } },
  { id: "vw-polo-vivo-comfortline", make: "Volkswagen", model: "Polo Vivo", variant: "2018-2023 1.4 Comfortline", size: { width: 185, aspectRatio: 60, rimDiameter: 15 } },
  { id: "vw-golf-7-trendline", make: "Volkswagen", model: "Golf", modelAliases: ["Golf 7", "Golf VII"], variant: "2013-2017 Golf 7 1.2 TSI Trendline, 16-inch option", size: { width: 205, aspectRatio: 55, rimDiameter: 16 } },
  { id: "toyota-corolla-quest", make: "Toyota", model: "Corolla Quest", variant: "2020-2023 1.8 standard, 15-inch option", size: { width: 195, aspectRatio: 65, rimDiameter: 15 } },
  { id: "toyota-hilux-raider", make: "Toyota", model: "Hilux", variant: "2016-2020 2.8 GD-6 Raider double cab, 17-inch option", size: { width: 265, aspectRatio: 65, rimDiameter: 17 } },
  { id: "ford-ranger-xlt", make: "Ford", model: "Ranger", variant: "2016-2021 3.2 TDCi XLT double cab, 17-inch option", size: { width: 265, aspectRatio: 65, rimDiameter: 17 } },
];

export function getVehicleWheelFitments(categoryNames: string[]): VehicleWheelFitment[] {
  const normalize = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const names = categoryNames.map(normalize);
  const path = names.join(" ");
  return VEHICLE_WHEEL_FITMENTS.filter((fitment) => {
    const make = normalize(fitment.make);
    const models = [fitment.model, ...(fitment.modelAliases ?? [])].map(normalize);
    return models.some((model) =>
      path === `${make} ${model}` ||
      (names.includes(make) && names.includes(model)) ||
      names.includes(`${make} ${model}`)
    );
  });
}
